import { useState } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  User,
  Save,
} from 'lucide-react';
import { useUsuarios, useDeleteUsuario } from '../../hooks/useUsuarios';
import { useRoles } from '../../hooks/useRoles';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { toast } from 'sonner';
import api from '../../api/clients';

export function Usuarios() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState<any>(null);
  const [formData, setFormData] = useState({
    usuario: '',
    contrasena: '',
    nombre: '',
    descripcion: '',
    email: '',
    role: 'USER',
    rolId: null as number | null,
    activo: true,
  });

  const { data: usuarios, isLoading, refetch } = useUsuarios();
  const { data: roles } = useRoles();
  const deleteUsuario = useDeleteUsuario();

  const handleDelete = async (id: number, usuario: string) => {
    if (!confirm(`¿Estás seguro de eliminar el usuario "${usuario}"?`)) return;
    try {
      await deleteUsuario.mutateAsync(id);
      refetch();
    } catch (error) {
      // Error ya manejado en el hook
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.usuario.trim()) {
        toast.error('El nombre de usuario es requerido');
        return;
      }
      if (!editingUsuario && !formData.contrasena.trim()) {
        toast.error('La contraseña es requerida');
        return;
      }
      if (formData.contrasena && formData.contrasena.length < 4) {
        toast.error('La contraseña debe tener al menos 4 caracteres');
        return;
      }

      if (editingUsuario) {
        const updateData: any = {
          usuario: formData.usuario,
          nombre: formData.nombre,
          descripcion: formData.descripcion,
          email: formData.email,
          role: formData.role,
          rolId: formData.rolId,
          activo: formData.activo,
        };
        if (formData.contrasena) {
          updateData.contrasena = formData.contrasena;
        }
        await api.put(`/usuarios/${editingUsuario.id}`, updateData);
        toast.success('Usuario actualizado exitosamente');
      } else {
        await api.post('/usuarios', {
          usuario: formData.usuario,
          contrasena: formData.contrasena,
          nombre: formData.nombre,
          descripcion: formData.descripcion,
          email: formData.email,
          role: formData.role,
          rolId: formData.rolId,
          activo: formData.activo,
        });
        toast.success('Usuario creado exitosamente');
      }
      refetch();
      setShowModal(false);
      setEditingUsuario(null);
      setFormData({
        usuario: '',
        contrasena: '',
        nombre: '',
        descripcion: '',
        email: '',
        role: 'USER',
        rolId: null,
        activo: true,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar el usuario');
    }
  };

  const handleEdit = (usuario: any) => {
    setEditingUsuario(usuario);
    setFormData({
      usuario: usuario.usuario,
      contrasena: '',
      nombre: usuario.nombre || '',
      descripcion: usuario.descripcion || '',
      email: usuario.email || '',
      role: usuario.role || 'USER',
      rolId: usuario.rolId || null,
      activo: usuario.activo,
    });
    setShowModal(true);
  };

  const filteredUsuarios = usuarios?.filter((u: any) =>
    u.usuario.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando usuarios...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Usuarios</h1>
          <p className="text-sm text-gray-500">
            {filteredUsuarios.length} usuario(s) registrados
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingUsuario(null);
            setFormData({
              usuario: '',
              contrasena: '',
              nombre: '',
              descripcion: '',
              email: '',
              role: 'USER',
              rolId: null,
              activo: true,
            });
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus size={18} />
          Nuevo Usuario
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar usuarios por nombre o usuario..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsuarios.map((usuario: any) => (
          <Card key={usuario.id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-blue-500" />
                  <h3 className="font-semibold text-gray-800 truncate">{usuario.usuario}</h3>
                  {usuario.activo ? (
                    <Badge variant="success">Activo</Badge>
                  ) : (
                    <Badge variant="default">Inactivo</Badge>
                  )}
                </div>
                {usuario.nombre && (
                  <p className="text-sm text-gray-600 mt-1">{usuario.nombre}</p>
                )}
                {usuario.descripcion && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{usuario.descripcion}</p>
                )}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(usuario)}
                  className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(usuario.id, usuario.usuario)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1 text-sm">
              {usuario.rol && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Rol:</span>
                  <span className="font-medium text-gray-700">{usuario.rol.nombre}</span>
                </div>
              )}
              {usuario.role && usuario.role !== 'USER' && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Rol antiguo:</span>
                  <span className="font-medium text-gray-700">{usuario.role}</span>
                </div>
              )}
              {usuario.email && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="text-gray-600 truncate max-w-30">{usuario.email}</span>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {filteredUsuarios.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <User size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron usuarios</p>
        </div>
      )}

      {/* Modal de Crear/Editar Usuario */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingUsuario(null);
        }}
        title={editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Usuario *
            </label>
            <Input
              value={formData.usuario}
              onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
              placeholder="Ej: pedro"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña {editingUsuario ? '(dejar en blanco para no cambiar)' : '*'}
            </label>
            <Input
              type="password"
              value={formData.contrasena}
              onChange={(e) => setFormData({ ...formData, contrasena: e.target.value })}
              placeholder={editingUsuario ? 'Nueva contraseña (opcional)' : 'Ingresa una contraseña'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre
            </label>
            <Input
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Pedro García"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Ej: Pedro ventas"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="pedro@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol
            </label>
            <select
              value={formData.rolId || ''}
              onChange={(e) => setFormData({ ...formData, rolId: e.target.value ? Number(e.target.value) : null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Sin rol</option>
              {roles?.map((rol: any) => (
                <option key={rol.id} value={rol.id}>{rol.nombre}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded border-gray-300"
            />
            <label className="text-sm text-gray-700">Usuario activo</label>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save size={18} />
              {editingUsuario ? 'Actualizar' : 'Crear'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setEditingUsuario(null);
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}